import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DataTable from "../../components/admin/DataTable";
import AdminDialog from "../../components/admin/AdminDialog";
import { setImpersonation } from "../../lib/impersonation";
import { useAuth } from "../../context/AuthContext";

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sellers/admin/all");
      setSellers(data);
    } catch (e) {
      toast.error("Failed to load sellers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    if (!window.confirm(`Change seller status to ${status}?`)) return;
    try {
      await api.put(`/admin/sellers/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const openDetail = async (s) => {
    try {
      const { data } = await api.get(`/admin/sellers/${s.id}`);
      setDetail(data);
    } catch { toast.error("Failed to load seller"); }
  };

  const updateCommission = async () => {
    const pct = Number(window.prompt("New commission percent (0-50):", detail.seller.commission_percent ?? 10));
    if (Number.isNaN(pct) || pct < 0 || pct > 50) return toast.error("Invalid percent");
    try {
      await api.put(`/admin/sellers/${detail.seller.id}/commission`, { commission_percent: pct });
      toast.success("Commission updated");
      await openDetail(detail.seller);
      load();
    } catch { toast.error("Failed"); }
  };

  const impersonate = async (s) => {
    if (!window.confirm(`Sign in as "${s.shop_name}" and act on their behalf?`)) return;
    try {
      const { data } = await api.post(`/admin/sellers/${s.id}/impersonate/start`);
      setImpersonation(data);
      toast.success(`Acting as ${data.shop_name}`);
      await refresh();
      setDetail(null);
      navigate("/seller");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start impersonation");
    }
  };

  const columns = [
    {
      key: "shop", label: "Shop",
      render: (r) => <div><div className="font-medium">{r.shop_name}</div><div className="text-xs text-[hsl(var(--kb-muted))]">{r.shop_slug}</div></div>,
    },
    { key: "status", label: "Status", render: (r) => <span className={`uppercase text-xs ${r.status === "approved" ? "text-green-700" : r.status === "rejected" ? "text-red-700" : "text-[hsl(var(--kb-primary))]"}`}>{r.status}</span> },
    { key: "commission_percent", label: "Commission %", render: (r) => `${r.commission_percent ?? 10}%` },
    { key: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
    {
      key: "actions", label: "",
      render: (r) => (
        <div className="flex gap-2 flex-wrap">
          <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => openDetail(r)} data-testid={`view-${r.shop_slug}`}>View</button>
          <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => impersonate(r)} data-testid={`impersonate-${r.shop_slug}`}>Impersonate</button>
          {r.status !== "approved" && <button className="kb-btn-primary !py-2 !px-3 !text-xs" onClick={() => updateStatus(r.id, "approved")} data-testid={`approve-${r.shop_slug}`}>Approve</button>}
          {r.status !== "rejected" && <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => updateStatus(r.id, "rejected")} data-testid={`reject-${r.shop_slug}`}>Reject</button>}
          {r.status === "approved" && <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => updateStatus(r.id, "suspended")}>Suspend</button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-sellers-page">
      <div className="overline mb-2">Verification</div>
      <h1 className="font-display text-4xl mb-8">Sellers</h1>

      <DataTable columns={columns} rows={sellers} loading={loading} emptyLabel="No sellers yet" testid="sellers-table" />

      {detail && (
        <AdminDialog
          open={!!detail}
          onClose={() => setDetail(null)}
          title={detail.seller.shop_name}
          subtitle="Atelier"
          maxWidth="max-w-2xl"
          testid="seller-detail-panel"
        >
          <section>
            <div className="overline mb-3">Contact</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><b>Name:</b> {detail.user?.name || "—"}</div>
              <div><b>Phone:</b> {detail.user?.phone}</div>
              <div><b>Email:</b> {detail.user?.email || "—"}</div>
              <div><b>Status:</b> <span className="uppercase">{detail.seller.status}</span></div>
            </div>
          </section>
          <section>
            <div className="overline mb-3">Compliance (KYC)</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><b>Legal Name:</b> {detail.seller.legal_name || "—"}</div>
              <div><b>Business Type:</b> {detail.seller.business_type || "—"}</div>
              <div><b>GSTIN:</b> {detail.seller.gstin || "—"}</div>
              <div><b>PAN:</b> {detail.seller.pan || "—"}</div>
            </div>
          </section>
          <section>
            <div className="overline mb-3">Bank</div>
            {detail.seller.bank ? (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><b>Holder:</b> {detail.seller.bank.account_holder}</div>
                <div><b>Bank:</b> {detail.seller.bank.bank_name || "—"}</div>
                <div><b>IFSC:</b> {detail.seller.bank.ifsc}</div>
                <div><b>A/c:</b> {detail.seller.bank.account_number}</div>
              </div>
            ) : <div className="text-sm text-[hsl(var(--kb-muted))]">Bank details not submitted</div>}
          </section>
          <section>
            <div className="overline mb-3">Commission</div>
            <div className="flex items-center justify-between text-sm">
              <div>{detail.seller.commission_percent ?? 10}%</div>
              <button onClick={updateCommission} className="kb-btn-outline !py-2 !px-3 !text-xs" data-testid="edit-commission">Edit</button>
            </div>
          </section>
          <section>
            <div className="overline mb-3">Performance</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><b>Products:</b> {detail.performance.products_count}</div>
              <div><b>Orders:</b> {detail.performance.orders_count}</div>
              <div><b>Gross:</b> ₹{detail.performance.gross_sales}</div>
              <div><b>Payout:</b> ₹{detail.performance.seller_payout}</div>
            </div>
          </section>
          <section className="flex justify-end pt-2 border-t border-[hsl(var(--kb-border))]">
            <button
              onClick={() => impersonate(detail.seller)}
              className="kb-btn-primary !py-2 !px-4 !text-xs"
              data-testid="impersonate-from-detail"
            >
              Sign in as this seller
            </button>
          </section>
        </AdminDialog>
      )}
    </div>
  );
}
